/**
 * Help pages (sections/kh-info.liquid; design/updates/help-pages, design/updates/contacts):
 * - «Питання» accordions (independent); under 1100 px the «Допомога» fold with the list of help pages
 *   (no sub-section navigation — Олександр 06.10, the texts are short);
 * - contacts: «Скопіювати» the e-mail — Mint «Скопійовано» for 2 s;
 * - tabs: a click on another help page swaps the section in place and changes the URL (own link to share); the other
 *   pages are prefetched once the page is idle; back / forward work; a page on another template opens normally.
 */
/** @type {Map<string, Promise<string | null>>} */
const cache = new Map();

/** @param {string} href */
const keyOf = (href) => new URL(href, window.location.origin).pathname;

/** The section's HTML for a help page, or null if that page is not on this template. @param {string} href @param {string} sectionId */
function fetchTab(href, sectionId) {
  const key = keyOf(href);
  if (!cache.has(key)) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    cache.set(
      key,
      fetch(url)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => (html && html.includes('data-kh-help') ? html : null))
        .catch(() => null)
    );
  }
  return /** @type {Promise<string | null>} */ (cache.get(key));
}

/** The element whose scrolling moves the page (Horizon scrolls .page-wrapper in some modes). */
function scroller() {
  const wrapper = document.querySelector('.page-wrapper');
  return wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible'
    ? wrapper
    : document.scrollingElement || document.documentElement;
}

class KhHelp extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#init();
    window.addEventListener('popstate', this.#onPop);
    const prefetch = () => this.querySelectorAll('[data-kh-help-tab]').forEach((a) => fetchTab(/** @type {HTMLAnchorElement} */ (a).href, this.dataset.sectionId || ''));
    if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 3000 });
    else window.setTimeout(prefetch, 1500);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.removeEventListener('popstate', this.#onPop);
  }

  /** Everything that lives inside the swapped markup. */
  #init() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    // The current page is already here: «back» to it is a swap too, not a reload
    const here = keyOf(window.location.href);
    if (!cache.has(here)) cache.set(here, Promise.resolve(this.outerHTML));
    this.addEventListener('click', this.#onClick, { signal });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // «Питання» and the «Допомога» fold
    const qa = target.closest('.qa__head');
    if (qa) return this.#fold(/** @type {HTMLElement} */ (qa), qa.getAttribute('aria-expanded') !== 'true');
    const mtocHead = target.closest('.kh-help__mtoc-head');
    if (mtocHead) return this.#fold(/** @type {HTMLElement} */ (mtocHead), mtocHead.getAttribute('aria-expanded') !== 'true');

    // Contacts: copy the e-mail
    const copy = /** @type {HTMLElement | null} */ (target.closest('[data-kh-copy]'));
    if (copy) return void this.#copy(copy);

    // Another help page
    const tab = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-kh-help-tab]'));
    if (tab && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      if (tab.getAttribute('aria-current') === 'page') return event.preventDefault();
      event.preventDefault();
      void this.#go(tab.href, true);
    }
  };

  /** @param {HTMLElement} button @param {boolean} open */
  #fold(button, open) {
    button.setAttribute('aria-expanded', String(open));
    document.getElementById(button.getAttribute('aria-controls') || '')?.classList.toggle('is-open', open);
  }

  /** @param {HTMLElement} button */
  async #copy(button) {
    const text = button.dataset.khCopy || '';
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.cssText = 'position:fixed;opacity:0';
      document.body.append(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      if (!ok) return; // could not copy — the button stays as it is
    }
    const label = button.querySelector('[data-kh-copy-label]');
    const icon = /** @type {HTMLElement | null} */ (button.querySelector('[data-kh-copy-icon]'));
    const done = /** @type {HTMLElement | null} */ (button.querySelector('[data-kh-copy-done]'));
    const live = this.querySelector('[data-kh-copy-live]');
    if (label) label.textContent = this.dataset.copied || '';
    if (icon) icon.hidden = true;
    if (done) done.hidden = false;
    button.classList.add('is-done');
    if (live) live.textContent = this.dataset.copiedLive || '';
    window.setTimeout(() => {
      if (label) label.textContent = text;
      if (icon) icon.hidden = false;
      if (done) done.hidden = true;
      button.classList.remove('is-done');
      if (live) live.textContent = '';
    }, 2000);
  }

  /** @param {string} href @param {boolean} push */
  async #go(href, push) {
    const html = await fetchTab(href, this.dataset.sectionId || '');
    if (!html) {
      window.location.href = href; // not a help page on this template
      return;
    }
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('kh-help');
    if (!fresh) {
      window.location.href = href;
      return;
    }
    if (push) history.pushState({ khHelp: true }, '', href);
    this.className = fresh.className;
    this.replaceChildren(...[...fresh.childNodes].map((n) => document.importNode(n, true)));
    document.title = `${this.querySelector('.kh-help__title')?.textContent?.trim() || ''}${this.dataset.titleSuffix || ''}`;
    const s = scroller();
    if (s.scrollTop > 0) s.scrollTo({ top: 0, behavior: 'auto' });
    this.#init();
    /** @type {HTMLElement | null} */ (this.querySelector('[data-kh-help-tab][aria-current="page"]'))?.focus({ preventScroll: true });
  }

  #onPop = () => {
    const current = this.querySelector('[data-kh-help-tab][aria-current="page"]');
    if (current && keyOf(/** @type {HTMLAnchorElement} */ (current).href) === keyOf(window.location.href)) return;
    void this.#go(window.location.href, false);
  };
}

if (!customElements.get('kh-help')) customElements.define('kh-help', KhHelp);
